import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import * as AccordionPrimitive from '@radix-ui/react-accordion';
import { MessageSquare } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { SupportDialog } from '@/components/support/SupportDialog';
import { Magnetic } from '@/components/fx/Magnetic';
import { FM_EASE_OUT, hasFinePointer, isSmallScreen } from '@/lib/motion';

// Key stems only — the question/answer copy lives in the locale bundles so the
// whole accordion re-renders in Spanish when the toggle flips.
const FAQ_KEYS = ['whatIs', 'start', 'approve', 'content', 'time', 'commercial', 'refund', 'api'] as const;
export type FaqKey = (typeof FAQ_KEYS)[number];
// Home shows five (Studio Blueprint 02, screen 5); studio pages show all eight.
export const HOME_FAQ_KEYS: readonly FaqKey[] = ['whatIs', 'start', 'approve', 'time', 'refund'];

// FAQ (immersive redesign, 2026-09-24): on large screens the heading and its
// intro stay pinned on the left while the questions scroll on the right; on
// phones they stack. Rows are at least 52px tall so a thumb can't miss, and
// answers open with a height + fade on the page's expo-out curve (the
// faq-open / faq-close keyframes in tailwind.config.ts). The support button
// opens the same form as the navbar's Customer Support.
export function FAQSection({ keys = FAQ_KEYS }: { keys?: readonly FaqKey[] } = {}) {
  const { t } = useTranslation();
  const reduceMotion = useReducedMotion();

  // One reveal for everything here: rise 24px and fade, once. Reduced motion
  // gets the end state immediately, and so do phones and touch screens
  // (2026-09-25), the stops' revealFrom rule: after a jump to #faq a busy
  // phone held the heading and the questions at opacity 0 for a beat.
  // Read once, on mount, like framer reads `initial`.
  const [revealOnScroll] = useState(() => !isSmallScreen() && hasFinePointer());
  // Keyboard focus anywhere in the section finishes the reveal at once, so
  // Tab never lands on a question or the support button mid-fade. So does
  // a link to #faq: the jump lands on the finished section, not on a blank
  // one that fades in after it (a loaded desktop held it half-faded for
  // seconds).
  // `done` reads the hash in the same render that brings it (a re-render
  // from the effect landed up to a second after the jump on a busy page);
  // `shown` keeps it done once the hash moves on.
  const { hash } = useLocation();
  const [shown, setShown] = useState(false);
  const done = shown || hash === '#faq';
  useEffect(() => {
    if (hash === '#faq') setShown(true);
  }, [hash]);
  const reveal = (delay = 0) => {
    if (reduceMotion || !revealOnScroll) return {};
    if (done) return { animate: { opacity: 1, y: 0 }, transition: { duration: 0 } };
    return {
      initial: { opacity: 0, y: 24 },
      whileInView: { opacity: 1, y: 0 },
      viewport: { once: true, margin: '0px 0px -10% 0px' },
      transition: { duration: 0.9, ease: FM_EASE_OUT, delay },
    };
  };

  // Phones get py-12 (2026-09-25): under the workspace tiles' own bottom
  // padding, py-16 left a ~105px empty band above the eyebrow. And
  // scroll-mt-8 there: with scroll-mt-2 the FAQ links parked the eyebrow
  // right under the 56px navbar; 2rem + py-12 sets it ~25px below.
  return (
    <section
      id="faq"
      onFocusCapture={() => { if (!shown) setShown(true); }}
      className="relative scroll-mt-8 py-12 sm:scroll-mt-2 sm:py-24 lg:py-28"
    >
      {/* A cool glow behind the heading column. */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -left-40 top-10 h-[28rem] w-[28rem] rounded-full bg-[radial-gradient(circle,hsl(184_52%_40%/0.12),transparent_65%)]" />
      </div>

      {/* md:px-8: the tablet gutter "how it works" uses (it was 16px here).
          Back to px-4 from xl, where max-w-6xl sets the edge the tool cards
          and the story share. */}
      <div className="container relative mx-auto px-4 md:px-8 xl:px-4">
        <div className="mx-auto grid max-w-6xl gap-10 lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)] lg:gap-20">
          {/* Heading column: sticky beside the list on lg+. */}
          <div className="lg:sticky lg:top-28 lg:self-start">
            <motion.p {...reveal()} className="eyebrow">
              {t('faq.title')}
            </motion.p>
            {/* The page's one section-title scale (2026-09-25), the tool
                cards' "Choose what to make": 28 / 32 / 44 / 56px. The old
                clamp ran to 61px on desktop, three lines that made this the
                heaviest heading on the page after the h1. */}
            <motion.h2
              {...reveal(0.06)}
              className="mt-5 text-balance font-display text-[28px] font-bold leading-[1.04] tracking-[-0.025em] min-[400px]:text-[32px] sm:text-[44px] lg:text-[56px]"
            >
              {t('faq.heading')} <span className="gradient-text">{t('faq.questions')}</span>
            </motion.h2>
            <motion.p {...reveal(0.12)} className="text-pretty mt-5 max-w-md text-[1rem] leading-relaxed text-muted-foreground sm:text-[1.05rem]">
              {t('faq.description')}
            </motion.p>
            <motion.div {...reveal(0.18)} className="mt-8">
              <Magnetic strength={0.25} radius={40}>
                <SupportDialog
                  trigger={
                    <button
                      type="button"
                      className="group inline-flex h-12 items-center gap-2.5 rounded-full border border-white/[0.12] bg-white/[0.04] pl-2 pr-5 text-[0.9rem] font-semibold text-foreground transition-[background-color,border-color,transform] duration-300 ease-expo-out hover:border-primary/50 hover:bg-white/[0.07] active:scale-[0.98]"
                    >
                      <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/15 text-primary transition-transform duration-500 ease-expo-out group-hover:rotate-[-8deg] group-hover:scale-110">
                        <MessageSquare className="h-4 w-4" aria-hidden="true" />
                      </span>
                      {t('immersiveChrome.faq.contactCta', { defaultValue: 'Contact support' })}
                    </button>
                  }
                />
              </Magnetic>
            </motion.div>
          </div>

          {/* The questions. */}
          <AccordionPrimitive.Root type="single" collapsible className="border-t border-white/[0.09]">
            {keys.map((key, index) => (
              <motion.div key={key} {...reveal(Math.min(index, 6) * 0.05)}>
                <AccordionPrimitive.Item value={`item-${index}`} className="group relative border-b border-white/[0.09]">
                  {/* Amber rule on the open row's leading edge. */}
                  <span
                    aria-hidden="true"
                    className="absolute -left-px top-0 h-full w-px origin-top scale-y-0 bg-[linear-gradient(180deg,hsl(var(--primary)),hsl(var(--accent)/0.4))] transition-transform duration-700 ease-expo-out group-data-[state=open]:scale-y-100"
                  />
                  <AccordionPrimitive.Header className="m-0">
                    <AccordionPrimitive.Trigger className="flex min-h-[3.25rem] w-full items-start gap-3 py-5 text-left transition-colors sm:gap-5 sm:py-6 [&[data-state=open]_.faq-plus]:rotate-45 [&[data-state=open]_.faq-plus]:border-primary/60 [&[data-state=open]_.faq-plus]:bg-primary/15 [&[data-state=open]_.faq-plus]:text-primary">
                      <span
                        aria-hidden="true"
                        className="w-9 shrink-0 pl-3 pt-[0.22rem] font-sans text-[11px] font-semibold tabular-nums tracking-[0.08em] sm:w-10 text-muted-foreground/60 transition-colors duration-300 group-hover:text-muted-foreground group-data-[state=open]:text-primary sm:pl-4"
                      >
                        {String(index + 1).padStart(2, '0')}
                      </span>
                      <span className="min-w-0 flex-1 text-[1.02rem] font-semibold leading-snug tracking-[-0.005em] text-foreground/85 transition-colors duration-300 group-hover:text-foreground group-data-[state=open]:text-foreground sm:text-[1.1rem]">
                        {t(`faq.items.${key}Q`)}
                      </span>
                      <span
                        aria-hidden="true"
                        className="faq-plus relative mt-[-0.1rem] flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-white/[0.12] text-muted-foreground transition-[transform,background-color,border-color,color] duration-500 ease-expo-out group-hover:border-white/25 group-hover:text-foreground"
                      >
                        <span className="absolute h-px w-3 bg-current" />
                        <span className="absolute h-3 w-px bg-current" />
                      </span>
                    </AccordionPrimitive.Trigger>
                  </AccordionPrimitive.Header>
                  <AccordionPrimitive.Content className="overflow-hidden data-[state=closed]:animate-faq-close data-[state=open]:animate-faq-open motion-reduce:animate-none">
                    {/* 15px floor on phones (0.98rem is 14.7px on the 15px phone root). */}
                    <p className="text-pretty pb-6 pl-12 pr-2 text-[15px] leading-relaxed text-muted-foreground sm:pb-7 sm:pl-[3.75rem] sm:pr-12 sm:text-[0.98rem]">
                      {t(`faq.items.${key}A`)}
                    </p>
                  </AccordionPrimitive.Content>
                </AccordionPrimitive.Item>
              </motion.div>
            ))}
          </AccordionPrimitive.Root>
        </div>
      </div>
    </section>
  );
}
